import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { NavLink } from 'react-router-dom';
import { Nav } from 'react-bootstrap';

interface AnimatedNavButtonProps {
    to:         string;
    eventKey:   number;
    children:   React.ReactNode;
    className?: string;
}

export function AnimatedNavButton({ to, eventKey, children, className = "" }: AnimatedNavButtonProps) {
    const ref = useRef<HTMLDivElement>(null);
    const [mousePosition,  setMousePosition]  = useState( { x: 0, y: 0 } );
    const [isHovered,      setIsHovered]      = useState( false );
    const [hoverIntensity, setHoverIntensity] = useState( 0     );

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if( !ref.current )
            return;
        const rect = ref.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        //calc distance from center
        const centerX     = rect.width / 2;
        const centerY     = rect.height / 2;
        const distance    = Math.sqrt( Math.pow( x - centerX, 2 ) + Math.pow( y - centerY, 2 ) );
        const maxDistance = Math.sqrt( Math.pow( centerX, 2 ) + Math.pow( centerY, 2 ) );
        const intensity   = Math.max( 0, 1 - ( distance / maxDistance ) );
        setMousePosition({ x, y });
        setHoverIntensity(intensity);
    };

    const buttonStyle = {
        color:          "black",
        isolation:      'isolate' as const,
        textDecoration: 'none',
        transition:     'transform 0.2s ease'
    };

    return (
        <Nav.Link
            ref={ref as any}
            eventKey={eventKey}
            as={NavLink}
            to={to}
            className={`${className} position-relative overflow-hidden px-3 rounded animated-nav-button`}
            style={buttonStyle}
            onMouseMove={handleMouseMove}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => {
                setIsHovered(false);
                setHoverIntensity(0);
            }}>
            {/* Subtle dark background on hover */}
            <motion.div
                className="position-absolute top-0 start-0 w-100 h-100 rounded"
                animate={{
                    opacity: isHovered ? hoverIntensity * 0.3 : 0,
                    backgroundColor: 'rgba(0, 0, 0, 1)'
                }}
                transition={{ duration: 0.1 }}
                style={{
                    zIndex: -2,
                    pointerEvents: 'none'
                }}
            />
            {/* Mouse-following gradient */}
            <motion.div
                className="position-absolute"
                animate={{
                    opacity: isHovered ? 1 : 0,
                    x: mousePosition.x - 150,
                    y: mousePosition.y - 150,
                }}
                transition={{
                    opacity: { duration: 0.2 },
                    x: { duration: 0 },
                    y: { duration: 0 }
                }}
                style={{
                    width:  300,
                    height: 300,
                    background: 'radial-gradient( circle, rgba(170, 204, 255, 0.5) 100%, rgba(255, 251, 0, 0.5) 50%, transparent 70%)',
                    pointerEvents: 'none',
                    zIndex: -1,
                    filter: 'blur(20px)',
                    mixBlendMode: 'screen'
                }}
            />
            <span className="position-relative" style={{ zIndex: 1 }}>
                {children}
            </span>
        </Nav.Link>
    );
}